import * as React from "react";
import { CircleCheck, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { formatAdvanceAmounts } from "@/lib/advance";
import { ApiError, discardMyRequest, fetchAdvance, fetchLinkableOtherAdvances } from "../../api";
import { Advance, OTHER_CLAIM_TYPE_LABEL, OtherClaimType } from "../../types";
import { formatDate } from "@/lib/datetime";

// An Other advance is named by its purpose (flightDetails) - it has no trip.
const describeAdvance = (a: Advance) =>
  `${a.flightDetails || "Advance"} - ${formatAdvanceAmounts(a.amounts)} - paid ${a.paidAt ? formatDate(a.paidAt) : ""}`;

/** The Other reimbursement wizard's "Did you already receive an advance?"
 *  step - LinkAdvanceField's twin for the Other flow: lists only the
 *  employee's own Paid Other advances of the same claim type (enforced
 *  again server-side - see requests_api.py's _other_advance_link_error),
 *  with the same linked badge + Unlink, and the same "Use this instead"
 *  way out for an advance held by one of their own Drafts. onChange may
 *  resolve to false to refuse the change (the server said no). */
export function OtherLinkAdvanceField({
  claimType,
  linkedAdvanceId,
  onChange,
  enabled,
  onToggleChange
}: {
  claimType: OtherClaimType;
  linkedAdvanceId: string | null;
  onChange: (advanceId: string | null, advance?: Advance) => void | boolean | Promise<void | boolean>;
  /** Whether the "Did you already receive an advance?" toggle is on, owned
   *  by the wizard, not this component (see LinkAdvanceField's twin prop -
   *  this component is remounted every time its own wizard step is
   *  revisited, so component-local toggle state would reset while the
   *  wizard's "block Next" copy of it stayed stuck, drifting out of sync
   *  with what the Switch visibly showed). */
  enabled: boolean;
  /** Fired whenever the toggle changes - lets the wizard block Next while
   *  it's on but nothing's picked yet (see LinkAdvanceField's twin prop). */
  onToggleChange: (enabled: boolean) => void;
}) {
  const [advances, setAdvances] = React.useState<Advance[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [known, setKnown] = React.useState<Advance | null>(null);
  const [discardTarget, setDiscardTarget] = React.useState<Advance | null>(null);
  const [discarding, setDiscarding] = React.useState(false);

  const loadAdvances = React.useCallback(() => {
    setLoading(true);
    return fetchLinkableOtherAdvances(claimType)
      .then(setAdvances)
      .catch(() => setAdvances([]))
      .finally(() => setLoading(false));
  }, [claimType]);
  React.useEffect(() => {
    void loadAdvances();
  }, [loadAdvances]);

  const pickable = advances.filter((a) => a.linkedRequestSummary?.status !== "Draft");
  const lockedInDraft = advances.filter((a) => a.linkedRequestSummary?.status === "Draft");

  const linked = linkedAdvanceId
    ? (advances.find((a) => a.id === linkedAdvanceId) ?? (known?.id === linkedAdvanceId ? known : null))
    : null;
  React.useEffect(() => {
    if (!linkedAdvanceId || loading || linked) return;
    fetchAdvance(linkedAdvanceId)
      .then(setKnown)
      .catch(() => {});
  }, [linkedAdvanceId, loading, linked]);

  const pick = async (advanceId: string) => {
    const advance = pickable.find((a) => a.id === advanceId);
    setBusy(true);
    try {
      if ((await onChange(advanceId, advance)) !== false && advance) setKnown(advance);
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (): Promise<boolean> => {
    setBusy(true);
    try {
      if ((await onChange(null)) === false) return false;
      setKnown(null);
      await loadAdvances();
      return true;
    } finally {
      setBusy(false);
    }
  };

  const discardAndFree = async () => {
    const draftId = discardTarget?.linkedRequestSummary?.id;
    if (!draftId) return;
    setDiscarding(true);
    try {
      await discardMyRequest(draftId);
      toast.success("Draft discarded - the advance is free to link now.");
      setDiscardTarget(null);
      await loadAdvances();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Could not discard that Draft - check your connection and try again."
      );
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>Did you already receive an advance?</Label>
          <p className="text-xs text-muted-foreground">
            Link it here so it's netted against this claim.
          </p>
        </div>
        <Switch
          checked={enabled || !!linkedAdvanceId}
          disabled={busy}
          onCheckedChange={async (checked) => {
            if (checked) onToggleChange(true);
            else if (!linkedAdvanceId || (await unlink())) onToggleChange(false);
          }}
        />
      </div>

      {(enabled || linkedAdvanceId) && (
        <div className="space-y-1.5">
          {linkedAdvanceId ? (
            <>
              <div className="flex items-start justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
                <div className="flex min-w-0 items-start gap-2">
                  <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="font-medium">Advance linked</p>
                    <p className="break-words text-xs text-muted-foreground">
                      {linked ? describeAdvance(linked) : "Loading advance…"}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => void unlink()}
                  disabled={busy}
                >
                  {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                  Unlink
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Unlink it to free the advance for another request.
              </p>
            </>
          ) : (
            <>
              <Label>Which advance?</Label>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading your advances…</p>
              ) : pickable.length === 0 && lockedInDraft.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  You have no Paid {OTHER_CLAIM_TYPE_LABEL[claimType]} advances available to link.
                  Raise one under Other Reimbursements → Advances first.
                </p>
              ) : (
                <>
                  {pickable.length > 0 && (
                    <Select value="" onValueChange={(v) => void pick(v)} disabled={busy}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an advance" />
                      </SelectTrigger>
                      <SelectContent>
                        {pickable.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {describeAdvance(a)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {lockedInDraft.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-start justify-between gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
                    >
                      <div className="flex min-w-0 items-start gap-2">
                        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                        <div className="min-w-0">
                          <p className="break-words font-medium">{describeAdvance(a)}</p>
                          <p className="text-xs text-muted-foreground">
                            Already linked to a Draft request you haven't submitted yet.
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => setDiscardTarget(a)}
                        disabled={busy}
                      >
                        Use this instead
                      </Button>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!discardTarget}
        onOpenChange={(open) => !open && setDiscardTarget(null)}
        title="Discard the other Draft?"
        description="This advance is linked to a Draft request you started earlier and haven't submitted. Discarding it will permanently delete that Draft - and any bills on it - and free the advance to link here instead."
        confirmLabel="Discard Draft & use this advance"
        pendingLabel="Discarding…"
        onConfirm={() => void discardAndFree()}
        pending={discarding}
        destructive
      />
    </div>
  );
}
